import type {
  ConnectionRequestIdPublic,
  EventIdPublic,
  EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import type { ConnectionRequestDirectionType } from "./directory-repository-options.types";
import type { DirectorySearchMode } from "@shared/schemas/directory-search-mode.schemas";

export const DIRECTORY_QUERY_KEYS = {
  all: ["directory"] as const,

  event: (eventIdPublic: EventIdPublic) =>
    ["directory", eventIdPublic] as const,

  // The names and meaning halves of one query are separate requests, so the
  // mode must be in the key or one half would be served the other's cache.
  entries: (
    eventIdPublic: EventIdPublic,
    searchQuery?: string,
    searchMode?: DirectorySearchMode,
  ) =>
    searchQuery !== undefined && searchQuery !== ""
      ? ([
          "directory",
          eventIdPublic,
          "entries",
          {
            search: searchQuery,
            mode: searchMode,
          },
        ] as const)
      : (["directory", eventIdPublic, "entries"] as const),

  connectionRequestsBase: (eventIdPublic: EventIdPublic) =>
    ["directory", eventIdPublic, "connection-requests"] as const,

  connectionRequests: (
    eventIdPublic: EventIdPublic,
    direction: ConnectionRequestDirectionType = "received",
  ) => ["directory", eventIdPublic, "connection-requests", direction] as const,

  connected: (eventIdPublic: EventIdPublic) =>
    ["directory", eventIdPublic, "connected"] as const,

  entry: (
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ) => ["directory", eventIdPublic, "entry", participantIdPublic] as const,

  profileDetail: (
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ) => ["directory", eventIdPublic, "profile", participantIdPublic] as const,

  bannerDismissed: (eventIdPublic: EventIdPublic) =>
    ["directory", eventIdPublic, "banner-dismissed"] as const,

  suggestions: (eventIdPublic: EventIdPublic) =>
    ["directory", eventIdPublic, "reasons"] as const,

  entriesBatch: (
    eventIdPublic: EventIdPublic,
    sortedParticipantIdsPublic: readonly EventParticipantIdPublic[],
  ) =>
    [
      "directory",
      eventIdPublic,
      "entries",
      "batch",
      sortedParticipantIdsPublic,
    ] as const,

  contactInfo: (
    eventIdPublic: EventIdPublic,
    requestIdPublic: ConnectionRequestIdPublic,
  ) => ["directory", eventIdPublic, "contact-info", requestIdPublic] as const,
} as const;
